import { useCallback, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Grid2 as Grid, Stack } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { isAllFieldsValid } from '../../../../utils/team';
import { Author, CreateAuthorInput } from '../types';
import { getAuthorSchema } from '../UI-schema';
import { useAuthors } from '../hooks/useAuthors';
import { useAppConfigOptions } from '../../../../hooks/appConfigs';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  author: Author;
}

function EditAuthorModal({ open, onClose, onDone, author }: Props) {
  const { strings } = useStringsContext();
  const { createAuthor, updateAuthor } = useAuthors();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>({});
  const editedFields = useRef<Record<string, boolean>>({});
  const { appConfigOptions } = useAppConfigOptions();

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);

    if (author.id) {
      await updateAuthor({
        id: author.id,
        ...valuesFields.current,
      });
    } else {
      await createAuthor(valuesFields.current as CreateAuthorInput);
    }

    onDone();
    onClose?.();
    setSubmitLoading(false);
  }, [author, onDone, onClose, createAuthor, updateAuthor]);

  const uiScheme = useMemo(() => {
    if (appConfigOptions) {
      return getAuthorSchema(appConfigOptions, author);
    }
  }, [appConfigOptions, author]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.articles.editAuthor} maxWidth="md">
      <DialogContent dividers>
        <Grid container spacing={4}>
          {uiScheme?.fields.map((field) => (
            <Grid size={12} key={field.id}>
              <RenderModuleField
                field={field}
                setChange={onChange}
                setValidation={onValidation}
                path="author"
                imagePath="authors"
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} sx={{ justifyContent: 'right', flex: 1 }}>
          <Stack direction="row" gap={2}>
            <Button color="primary" onClick={onClose}>
              {strings.actions.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={submitDisabled || !isDirty}
              loading={submitLoading}
              onClick={onSubmit}
            >
              {strings.actions.save}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}

export default EditAuthorModal;
